import React, { useEffect, useState } from 'react';

import {
    App,
    Button,
    Form,
    FormProps,
    Input,
    Select,
    Switch,
    UploadFile
} from 'antd';

import dayjs from 'dayjs';
import { DATETIME_SERVER_FORMAT } from 'internal-constants';
import { ApartmentField } from 'lib/ApartmentField';
import { CpfField } from 'lib/CpfField';
import { UnexpectedTypeError } from 'lib/errors/UnexpectedTypeError';
import { isCpf, isVehiclePlate, makeRawCpf, makeRawVehiclePlate } from 'lib/helpers/Domain.helper';
import { makeFilterOption } from 'lib/helpers/Form.helper';
import { handleServiceError, hasServiceError } from 'lib/helpers/ServiceHelper';
import { useGuestsLogs } from 'lib/providers/GuestsLogsContextProvider';
import { Show } from 'lib/Show';
import { UploadField } from 'lib/UploadField';
import { VehiclePlateField } from 'lib/VehiclePlateField';
import { CreateGuestLog, CreateGuestVehicle } from 'services/contracts/Guest.contract';
import {
    createGuest as createGuestService,
    createGuestLog,
    createGuestVehicle,
    findGuest,
    findGuestVehicle
} from 'services/Guest.service';
import { Guest } from 'types/Guest.type';

import { GuestAlert } from './GuestAlert';
import { VehicleAlert } from './VehicleAlert';

type Values = {
    apartment: Apartment.Model['id'],
    guest_cpf: string,
    guest_name: string,
    guest_picture: [UploadFile] | undefined | [],
    enteredAt: dayjs.Dayjs,
    reserve_id: number | null,
    reason: string | null,
    has_vehicle: boolean,
    vehicle_plate: string,
    vehicle_brand: string | null | undefined,
    vehicle_model: string | null | undefined,
    vehicle_color: string | null | undefined,
}

export function CreateGuestLogForm() {
    const [isCreating, setIsCreating] = useState(false);
    const [isCreatingGuest, setIsCreatingGuest] = useState(false);
    const [isCreatingVehicle, setIsCreatingVehicle] = useState(false);

    const [isFetchingGuest, setIsFetchingGuest] = useState(false);
    const [isFetchingVehicle, setIsFetchingVehicle] = useState(false);

    const [guest, setGuest] = useState<Guest.Model | null>(null);
    const [vehicle, setVehicle] = useState<Guest.Vehicle.Model | null>(null);

    const isKnowGuest = guest !== null;
    const isKnowVehicle = vehicle !== null;

    const [form] = Form.useForm<Values>();

    const watchedGuestCpf = Form.useWatch('guest_cpf', form);

    const watchedHasVehicle = Form.useWatch('has_vehicle', form);
    const watchedVehiclePlate = Form.useWatch('vehicle_plate', form);

    const { reserves, fetchGuestsLogs } = useGuestsLogs();

    const app = App.useApp();
    const reservesOptions = reserves.map(reserve => ({ value: reserve.id, label: `${reserve.ambient.name} (${reserve.apartment.tower?.name} - ${reserve.apartment.name})` }));

    useEffect(() => {
        const fetchGuest = async () => {
            if (!isCpf(watchedGuestCpf)) {
                form.setFieldsValue({
                    guest_name: undefined,
                    guest_picture: undefined
                });

                return;
            }

            setIsFetchingGuest(true);

            const rawCpf = makeRawCpf(watchedGuestCpf);

            const response = await findGuest(rawCpf);

            setIsFetchingGuest(false);

            if (hasServiceError(response))
                return handleServiceError(app, response);

            const { guest } = response;

            if (guest === null){
                setGuest(null);
                return;
            }

            const values: Partial<Values> = {
                guest_name: guest.name,
                guest_picture: guest.picture !== null ? [{
                    uid: guest.id.toString(),
                    name: '',
                    url: guest.picture,
                    status: 'done'
                }] : undefined
            };

            form.setFieldsValue(values);

            setGuest(guest);
        };

        fetchGuest();
    }, [app, form, watchedGuestCpf]);

    useEffect(() => {
        const fetchVehicle = async () => {
            if (!isVehiclePlate(watchedVehiclePlate)) {
                form.setFieldsValue({
                    vehicle_brand: undefined,
                    vehicle_model: undefined,
                    vehicle_color: undefined,
                });

                return;
            }

            setIsFetchingVehicle(true);

            const rawVehiclePlate = makeRawVehiclePlate(watchedVehiclePlate);

            const response = await findGuestVehicle(rawVehiclePlate);

            setIsFetchingVehicle(false);

            if (hasServiceError(response))
                return handleServiceError(app, response);

            const { guest_vehicle } = response;

            if (guest_vehicle === null)
                return;

            const values: Partial<Values> = {
                vehicle_brand: guest_vehicle.brand,
                vehicle_model: guest_vehicle.model,
                vehicle_color: guest_vehicle.color,
            };

            form.setFieldsValue(values);

            setVehicle(guest_vehicle);
        };

        fetchVehicle();
    }, [app, form, watchedVehiclePlate]);

    const createGuest = async ({ guest_cpf, guest_name, guest_picture = [] }: Values): Promise<Guest.Model | null> => {
        const picture = guest_picture[0];
        const parsedPicture = picture?.response || picture?.url;

        if (typeof parsedPicture !== 'string' && parsedPicture !== undefined)
            throw new UnexpectedTypeError('parsedPicture', typeof parsedPicture, 'string');

        if (!isCpf(guest_cpf))
            throw new UnexpectedTypeError('guest_cpf', typeof guest_cpf, 'Domain.Cpf');

        setIsCreatingGuest(true);

        const body = {
            name: guest_name,
            cpf: makeRawCpf(guest_cpf),
            picture: parsedPicture,
        };

        const response = await createGuestService(body);

        setIsCreatingGuest(false);

        app.notification.open(response);

        const localGuest = response.guest ?? null;

        return localGuest;
    };

    const createVehicle = async ({
        vehicle_plate,
        vehicle_brand,
        vehicle_model,
        vehicle_color,
    }: Values): Promise<Guest.Vehicle.Model | null> => {
        if (!isVehiclePlate(vehicle_plate))
            throw new UnexpectedTypeError('vehicle_plate', typeof vehicle_plate, 'Domain.Vehicle.Plate');

        setIsCreatingVehicle(true);

        const body: CreateGuestVehicle.Body = {
            plate: makeRawVehiclePlate(vehicle_plate),
            brand: vehicle_brand ?? null,
            model: vehicle_model ?? null,
            color: vehicle_color ?? null,
        };

        const response = await createGuestVehicle(body);

        setIsCreatingVehicle(false);

        app.notification.open(response);

        const localVehicle = response.guest_vehicle ?? null;

        return localVehicle;
    };

    const onFinish: FormProps<Values>['onFinish'] = async (values) => {

        const localGuest = !isKnowGuest
            ? await createGuest(values)
            : guest;

        let localVehicle = null;

        // Try again
        if (!localGuest)
            return;

        if (watchedHasVehicle.valueOf()) {
            localVehicle = !isKnowVehicle
                ? await createVehicle(values)
                : vehicle;

            // Try again
            if (!localVehicle)
                return;
        }

        setIsCreating(true);

        const body: CreateGuestLog.Body = {
            guest_id: localGuest.id,
            vehicle_id: localVehicle?.id ?? null,
            reserve_id: values.reserve_id ?? null,
            reason: values.reason ?? null,
            entered_at: dayjs().format(DATETIME_SERVER_FORMAT),
            apartment_id: values.apartment,
        };

        const response = await createGuestLog(body);

        setIsCreating(false);

        app.notification.open(response);

        // Try again, but with the recently created guest and vehicle to improves UX
        if (!('guest_log' in response)) {
            setGuest(localGuest);
            setVehicle(localVehicle);

            return;
        }

        form.resetFields();

        fetchGuestsLogs();
    };

    return (
        <Form<Values>
            form={form}
            onFinish={onFinish}
            initialValues={{ has_vehicle: false }}
            name="createGuestLog"
            layout="vertical"
        >
            {/* CAMPO PARA INDICAR SE UNIDADE ou RESERVA */}
            <ApartmentField
                rules={[{ required: true, message: 'Por favor, selecione uma unidade.' }]}
            />

            <CpfField
                name="guest_cpf"
                prefix={null}
                rules={[{ required: true, message: 'Por favor, digite o CPF do visitante.' }]}
                help="Para colaboradores que não possuem CPF, registre a matrícula e complete com zeros."
                loading={isFetchingGuest}
            />

            <Show when={isCpf(watchedGuestCpf) && !isFetchingGuest}>
                <GuestAlert
                    isKnowGuest={isKnowGuest}
                    style={{ marginBottom: 'var(--ant-form-item-margin-bottom)' }}
                />

                <Form.Item<Values>
                    name="guest_name"
                    label="Nome do visitante"
                    rules={[{ required: true, message: 'Por favor, digite o nome do visitante.' }]}
                >
                    <Input
                        maxLength={256}
                        placeholder="Digite o nome do visitante"
                        disabled={isKnowGuest}
                    />
                </Form.Item>

                <UploadField
                    name="guest_picture"
                    type="picture-card"
                    label="Foto de perfil do visitante"
                    maxCount={1}
                    multiple={false}
                    accept="image/png, image/jpeg"
                    disabled={isKnowGuest}
                />
            </Show>

            <Form.Item<Values>
                name="reserve_id"
                label="Reserva"
                rules={[{ required: false }]}
            >
                <Select allowClear options={reservesOptions} />
            </Form.Item>

            <Form.Item<Values>
                name="reason"
                label="Motivo da visita"
                rules={[{ required: true }]}
            >
                <Select
                    allowClear
                    showSearch
                    filterOption={makeFilterOption()}
                    options={[
                        { value: 'Mudança', label: 'Mudança' },
                        { value: 'Descarregamento de itens', label: 'Descarregamento de itens' },
                        { value: 'Transporte de aplicativo (Uber, 99, Táxi)', label: 'Transporte de aplicativo (Uber, 99, Táxi)' },
                        { value: 'Atendimento médico (ambulância, SAMU)', label: 'Atendimento médico (ambulância, SAMU)' },
                        { value: 'Desembarque de idoso ou pessoa com mobilidade reduzida', label: 'Desembarque de idoso ou pessoa com mobilidade reduzida' },
                        { value: 'Entrega de produtos (mercadorias, encomendas)', label: 'Entrega de produtos (mercadorias, encomendas)' },
                        { value: 'Retirada de produtos (móveis, eletros, encomendas)', label: 'Retirada de produtos (móveis, eletros, encomendas)' },
                        { value: 'Prestação de serviço (encanador, eletricista, limpeza, etc.)', label: 'Prestação de serviço (encanador, eletricista, limpeza, etc.)' },
                        { value: 'Visita social (familiares, amigos)', label: 'Visita social (familiares, amigos)' },
                        { value: 'Atendimento RGE', label: 'Atendimento RGE' }
                    ]}
                />
            </Form.Item>

            <Form.Item<Values>
                name="has_vehicle"
                label="Veículo identificável"
                rules={[{ required: true, message: 'Por favor, seleciona uma opção.' }]}
            >
                <Switch
                    checkedChildren="Sim (Carro ou moto)"
                    unCheckedChildren="Não (A pé, bicicleta)"
                />
            </Form.Item>

            <Show when={watchedHasVehicle}>
                <VehiclePlateField
                    rules={[{ required: true, message: 'Por favor, digite a placa do veículo.' }]}
                    loading={isFetchingVehicle}
                />

                <Show when={isVehiclePlate(watchedVehiclePlate) && !isFetchingVehicle}>
                    <VehicleAlert
                        isKnowVehicle={isKnowVehicle}
                        style={{ marginBottom: 'var(--ant-form-item-margin-bottom)' }}
                    />

                    <Form.Item<Values>
                        name="vehicle_brand"
                        label="Marca do veículo"
                    >
                        <Input
                            maxLength={256}
                            placeholder="Fiat, Hyundai"
                            disabled={isKnowVehicle}
                        />
                    </Form.Item>

                    <Form.Item<Values>
                        name="vehicle_model"
                        label="Modelo do veículo"
                    >
                        <Input
                            maxLength={256}
                            placeholder="Palio, HB20"
                            disabled={isKnowVehicle}
                        />
                    </Form.Item>

                    <Form.Item<Values>
                        name="vehicle_color"
                        label="Cor do veículo"
                    >
                        <Input
                            maxLength={256}
                            placeholder="Prata, Branco"
                            disabled={isKnowVehicle}
                        />
                    </Form.Item>
                </Show>
            </Show>

            <Button
                type="primary"
                htmlType="submit"
                loading={isCreating || isCreatingGuest || isCreatingVehicle}
                disabled={isFetchingGuest || isFetchingVehicle}
                block
            >
                Cadastrar
            </Button>
        </Form>
    );
}
